import { useState } from 'react';
import {
  LayoutDashboard, ListTodo, Users, Code2, Building2,
  LogOut, CheckCircle2, ShieldCheck, BellPlus, Plus,
  Pencil, Trash2, X, Search, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge, TaskStatusBadge, PriorityBadge } from '@/components/ui/Badge';
import { Input, Textarea } from '@/components/ui/Input';
import { Avatar } from '@/components/ui/Avatar';
import { TaskDetailModal } from '@/components/features/TaskDetailModal';
import type {
  Agency, AuthUser, Developer, PasswordResetRequest,
  ProjectNotification, Task, TaskStatus,
} from '@/types';
import { formatDate, formatDateRelative } from '@/lib/utils';

type AdminTab = 'overview' | 'tasks' | 'clients' | 'developers' | 'agency';

interface AdminDashboardProps {
  user: AuthUser;
  tasks: Task[];
  allUsers: AuthUser[];
  pendingUsers: AuthUser[];
  passwordResetRequests: PasswordResetRequest[];
  notifications: ProjectNotification[];
  developers: Developer[];
  agency: Agency | null;
  onLogout: () => void;
  onApproveUser: (id: string) => Promise<void>;
  onApprovePasswordReset: (id: string) => Promise<void>;
  onCreateAlert: (data: { title: string; message: string }) => Promise<void>;
  onTaskAction: (taskId: string, action: string, data?: any) => Promise<void>;
  onCreateDeveloper: (data: any) => Promise<void>;
  onUpdateDeveloper: (id: string, data: any) => Promise<void>;
  onDeleteDeveloper: (id: string) => Promise<void>;
  onUpdateAgency: (data: any) => Promise<void>;
  onUpdateUserRole: (id: string, role: 'admin' | 'client') => Promise<void>;
  onDeleteUser: (id: string) => Promise<void>;
}

const TABS: { id: AdminTab; label: string; Icon: any }[] = [
  { id: 'overview', label: 'Overview', Icon: LayoutDashboard },
  { id: 'tasks', label: 'Tasks', Icon: ListTodo },
  { id: 'clients', label: 'Clients', Icon: Users },
  { id: 'developers', label: 'Developers', Icon: Code2 },
  { id: 'agency', label: 'Agency', Icon: Building2 },
];

const STATUS_OPTS: { value: TaskStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'approved', label: 'Approved' },
];

const PACKAGE_HOURS: Record<string, number> = { starter: 40, growth: 80, sprint: 160 };

export function AdminDashboard({
  user, tasks, allUsers, pendingUsers, passwordResetRequests,
  notifications, developers, agency, onLogout,
  onApproveUser, onApprovePasswordReset, onCreateAlert,
  onTaskAction, onCreateDeveloper, onUpdateDeveloper, onDeleteDeveloper,
  onUpdateAgency, onUpdateUserRole, onDeleteUser,
}: AdminDashboardProps) {
  const [tab, setTab] = useState<AdminTab>('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Tasks tab state
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskStatusFilter, setTaskStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [taskPriorityFilter, setTaskPriorityFilter] = useState<'all' | 'standard' | 'urgent'>('all');
  const [taskSearch, setTaskSearch] = useState('');

  // Developer tab state
  const [showDevForm, setShowDevForm] = useState(false);
  const [editingDev, setEditingDev] = useState<Developer | null>(null);
  const [devForm, setDevForm] = useState({
    name: '', role: '', email: '', timezone: 'EST',
    workingHours: '9:00 AM - 6:00 PM', skills: '',
  });

  // Agency tab state
  const [agencyForm, setAgencyForm] = useState({
    name: agency?.name ?? '',
    packageType: (agency?.packageType ?? 'growth') as string,
    monthlyHours: String(agency?.monthlyHours ?? 80),
    extraHourRate: String(agency?.extraHourRate ?? 95),
    rolloverHours: String(agency?.rolloverHours ?? 0),
    hoursUsed: String(agency?.hoursUsed ?? 0),
    renewalDate: agency?.renewalDate?.slice(0, 10) ?? '',
  });
  const [savingAgency, setSavingAgency] = useState(false);

  // Alert form state
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [sendingAlert, setSendingAlert] = useState(false);

  // Filtered tasks
  const filteredTasks = tasks.filter((t) => {
    if (taskStatusFilter !== 'all' && t.status !== taskStatusFilter) return false;
    if (taskPriorityFilter !== 'all' && t.priority !== taskPriorityFilter) return false;
    if (taskSearch) {
      const q = taskSearch.toLowerCase();
      if (!t.title.toLowerCase().includes(q) && !t.description.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Task stats
  const taskStats = {
    total: tasks.length,
    queued: tasks.filter((t) => t.status === 'queued').length,
    inProgress: tasks.filter((t) => t.status === 'in-progress').length,
    review: tasks.filter((t) => t.status === 'review').length,
    delivered: tasks.filter((t) => t.status === 'delivered').length,
    approved: tasks.filter((t) => t.status === 'approved').length,
  };

  const openDevForm = (dev?: Developer) => {
    if (dev) {
      setEditingDev(dev);
      setDevForm({
        name: dev.name,
        role: dev.role,
        email: dev.email ?? '',
        timezone: dev.timezone,
        workingHours: dev.workingHours,
        skills: dev.skills.join(', '),
      });
    } else {
      setEditingDev(null);
      setDevForm({ name: '', role: '', email: '', timezone: 'EST', workingHours: '9:00 AM - 6:00 PM', skills: '' });
    }
    setShowDevForm(true);
  };

  const handleDevSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: devForm.name,
      role: devForm.role,
      email: devForm.email || undefined,
      timezone: devForm.timezone,
      workingHours: devForm.workingHours,
      skills: devForm.skills.split(',').map((s) => s.trim()).filter(Boolean),
    };
    if (editingDev) {
      await onUpdateDeveloper(editingDev.id, payload);
    } else {
      await onCreateDeveloper(payload);
    }
    setShowDevForm(false);
    setEditingDev(null);
  };

  const handleAgencySave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingAgency(true);
    try {
      await onUpdateAgency({
        name: agencyForm.name,
        packageType: agencyForm.packageType,
        monthlyHours: parseFloat(agencyForm.monthlyHours) || 80,
        extraHourRate: parseFloat(agencyForm.extraHourRate) || 95,
        rolloverHours: parseFloat(agencyForm.rolloverHours) || 0,
        hoursUsed: parseFloat(agencyForm.hoursUsed) || 0,
        renewalDate: agencyForm.renewalDate ? new Date(agencyForm.renewalDate).toISOString() : undefined,
      });
    } finally {
      setSavingAgency(false);
    }
  };

  const handleSendAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!alertTitle.trim() || !alertMessage.trim()) return;
    setSendingAlert(true);
    try {
      await onCreateAlert({ title: alertTitle, message: alertMessage });
      setAlertTitle('');
      setAlertMessage('');
    } finally {
      setSendingAlert(false);
    }
  };

  const handleTaskAction = async (taskId: string, action: string, data?: any) => {
    await onTaskAction(taskId, action, data);
    if (action === 'deleteTask') {
      setSelectedTask(null);
    } else {
      // Refresh the selected task from the updated tasks list (handled by parent)
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-lg font-bold text-white">O</div>
              <div>
                <h1 className="font-bold text-slate-900 leading-none">OrbitCrew Admin</h1>
                <p className="text-xs text-slate-500 mt-0.5">{user.email}</p>
              </div>
            </div>

            {/* Desktop tabs */}
            <nav className="hidden md:flex items-center gap-1">
              {TABS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tab === id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  {id === 'overview' && (pendingUsers.length > 0 || passwordResetRequests.length > 0) && (
                    <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                      {pendingUsers.length + passwordResetRequests.length}
                    </span>
                  )}
                  {id === 'tasks' && unreadCount > 0 && (
                    <span className="ml-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-bold text-white">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <Button variant="ghost" size="sm" leftIcon={<LogOut className="h-4 w-4" />} onClick={onLogout}>
                Sign out
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 px-4 py-3 space-y-1">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => { setTab(id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                  tab === id ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

        {/* ── OVERVIEW TAB ── */}
        {tab === 'overview' && (
          <div className="space-y-6">
            {/* Stats row */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {[
                { label: 'Total Tasks', value: taskStats.total, color: 'text-slate-900' },
                { label: 'Queued', value: taskStats.queued, color: 'text-slate-500' },
                { label: 'In Progress', value: taskStats.inProgress, color: 'text-blue-600' },
                { label: 'Review', value: taskStats.review, color: 'text-purple-600' },
                { label: 'Delivered', value: taskStats.delivered, color: 'text-orange-600' },
                { label: 'Approved', value: taskStats.approved, color: 'text-green-600' },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
              <div className="space-y-6">
                {/* Pending user approvals */}
                <div className="rounded-lg border border-slate-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <Users className="h-5 w-5 text-blue-600" />
                    <div>
                      <h2 className="font-bold text-slate-900">Client account approvals</h2>
                      <p className="text-sm text-slate-500">Approve registrations before clients can log in.</p>
                    </div>
                  </div>
                  {pendingUsers.length === 0 ? (
                    <p className="rounded-lg bg-slate-50 p-4 text-center text-sm text-slate-500">No pending registrations.</p>
                  ) : (
                    <div className="space-y-3">
                      {pendingUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{u.name}</p>
                              <Badge variant="warning" size="sm">Pending</Badge>
                            </div>
                            <p className="text-sm text-slate-500">{u.email}</p>
                            <p className="text-xs text-slate-400">Requested {formatDateRelative(u.createdAt)}</p>
                          </div>
                          <Button size="sm" leftIcon={<CheckCircle2 className="h-4 w-4" />} onClick={() => onApproveUser(u.id)}>
                            Approve
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Password reset approvals */}
                <div className="rounded-lg border border-slate-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <ShieldCheck className="h-5 w-5 text-blue-600" />
                    <div>
                      <h2 className="font-bold text-slate-900">Password reset approvals</h2>
                      <p className="text-sm text-slate-500">Review and approve client password reset requests.</p>
                    </div>
                  </div>
                  {passwordResetRequests.length === 0 ? (
                    <p className="rounded-lg bg-slate-50 p-4 text-center text-sm text-slate-500">No password reset requests.</p>
                  ) : (
                    <div className="space-y-3">
                      {passwordResetRequests.map((req) => (
                        <div key={req.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{req.email}</p>
                              <Badge
                                variant={req.status === 'pending' ? 'warning' : req.status === 'approved' ? 'success' : 'neutral'}
                                size="sm"
                              >
                                {req.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-400">Requested {formatDateRelative(req.createdAt)}</p>
                            {req.resetToken && (
                              <p className="mt-1 rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
                                Reset code: {req.resetToken}
                              </p>
                            )}
                          </div>
                          {req.status === 'pending' && (
                            <Button size="sm" leftIcon={<CheckCircle2 className="h-4 w-4" />} onClick={() => onApprovePasswordReset(req.id)}>
                              Approve
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Recent activity */}
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <h2 className="mb-4 font-bold text-slate-900">Recent notifications</h2>
                {notifications.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 py-4">No notifications yet.</p>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {notifications.slice(0, 12).map((n) => (
                      <div key={n.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-slate-900 text-sm">{n.title}</p>
                          <Badge variant={n.type === 'urgent' ? 'danger' : n.type === 'review' ? 'info' : 'neutral'} size="sm">
                            {n.type}
                          </Badge>
                          {!n.read && <span className="h-2 w-2 rounded-full bg-blue-500" />}
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{n.message}</p>
                        <p className="mt-1 text-xs text-slate-400">{formatDateRelative(n.timestamp)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── TASKS TAB ── */}
        {tab === 'tasks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">All Tasks ({tasks.length})</h2>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
                  placeholder="Search tasks..."
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                />
              </div>
              <select
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={taskStatusFilter}
                onChange={(e) => setTaskStatusFilter(e.target.value as any)}
              >
                {STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={taskPriorityFilter}
                onChange={(e) => setTaskPriorityFilter(e.target.value as any)}
              >
                <option value="all">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="standard">Standard</option>
              </select>
              {(taskStatusFilter !== 'all' || taskPriorityFilter !== 'all' || taskSearch) && (
                <button
                  className="text-sm text-slate-500 hover:text-slate-700 underline"
                  onClick={() => { setTaskStatusFilter('all'); setTaskPriorityFilter('all'); setTaskSearch(''); }}
                >
                  Clear filters
                </button>
              )}
              <span className="text-sm text-slate-500 ml-auto">{filteredTasks.length} result{filteredTasks.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Task list */}
            {filteredTasks.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
                <p className="text-slate-500">No tasks match your filters.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-300 transition-colors cursor-pointer group"
                    onClick={() => setSelectedTask(task)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <TaskStatusBadge status={task.status} />
                        <PriorityBadge priority={task.priority} />
                        <span className="text-xs text-slate-400 font-mono">#{task.id}</span>
                      </div>
                      <p className="font-semibold text-slate-900 truncate">{task.title}</p>
                      <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{task.description}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-slate-400 flex-wrap">
                        <span>Due {formatDate(task.estimatedCompletion)}</span>
                        {task.assignedDeveloper && (
                          <span className="flex items-center gap-1">
                            <Avatar name={task.assignedDeveloper.name} size="xs" />
                            {task.assignedDeveloper.name}
                          </span>
                        )}
                        <span>{task.comments.length} comment{task.comments.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <span className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 pt-1">
                      View →
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CLIENTS TAB ── */}
        {tab === 'clients' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900">All Clients ({allUsers.length})</h2>
            {allUsers.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
                <p className="text-slate-500">No client accounts yet.</p>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {['Name', 'Email', 'Role', 'Status', 'Joined', 'Actions'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {allUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Avatar name={u.name} size="sm" />
                              <span className="font-medium text-slate-900">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{u.email}</td>
                          <td className="px-4 py-3">
                            <Badge variant={u.role === 'admin' ? 'info' : 'neutral'} size="sm">{u.role}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant={u.status === 'approved' ? 'success' : u.status === 'pending' ? 'warning' : 'danger'}
                              size="sm"
                            >
                              {u.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-500">{formatDateRelative(u.createdAt)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {u.status === 'pending' && (
                                <button
                                  onClick={() => onApproveUser(u.id)}
                                  className="text-xs text-green-600 hover:text-green-700 font-medium border border-green-200 rounded px-2 py-1 hover:bg-green-50"
                                >
                                  Approve
                                </button>
                              )}
                              {u.status === 'approved' && u.id !== user.id && (
                                <>
                                  <button
                                    onClick={() => onUpdateUserRole(u.id, u.role === 'admin' ? 'client' : 'admin')}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium border border-blue-200 rounded px-2 py-1 hover:bg-blue-50"
                                    title={`Make ${u.role === 'admin' ? 'client' : 'admin'}`}
                                  >
                                    {u.role === 'admin' ? 'Demote' : 'Make Admin'}
                                  </button>
                                  <button
                                    onClick={() => { if (window.confirm(`Deactivate ${u.name}?`)) onDeleteUser(u.id); }}
                                    className="text-xs text-red-500 hover:text-red-600 font-medium border border-red-200 rounded px-2 py-1 hover:bg-red-50"
                                  >
                                    Deactivate
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── DEVELOPERS TAB ── */}
        {tab === 'developers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Developers ({developers.length})</h2>
              {!showDevForm && (
                <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => openDevForm()}>
                  Add Developer
                </Button>
              )}
            </div>

            {/* Developer form */}
            {showDevForm && (
              <form onSubmit={handleDevSubmit} className="rounded-lg border border-blue-200 bg-blue-50 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900">{editingDev ? 'Edit Developer' : 'Add Developer'}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Name *"
                    value={devForm.name}
                    onChange={(e) => setDevForm({ ...devForm, name: e.target.value })}
                    placeholder="Jane Smith"
                    required
                  />
                  <Input
                    label="Role *"
                    value={devForm.role}
                    onChange={(e) => setDevForm({ ...devForm, role: e.target.value })}
                    placeholder="Full-Stack Developer"
                    required
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={devForm.email}
                    onChange={(e) => setDevForm({ ...devForm, email: e.target.value })}
                    placeholder="jane@orbitcrew.app"
                  />
                  <Input
                    label="Timezone"
                    value={devForm.timezone}
                    onChange={(e) => setDevForm({ ...devForm, timezone: e.target.value })}
                    placeholder="EST"
                  />
                  <Input
                    label="Working Hours"
                    value={devForm.workingHours}
                    onChange={(e) => setDevForm({ ...devForm, workingHours: e.target.value })}
                    placeholder="9:00 AM - 6:00 PM"
                  />
                  <Input
                    label="Skills (comma-separated)"
                    value={devForm.skills}
                    onChange={(e) => setDevForm({ ...devForm, skills: e.target.value })}
                    placeholder="React, TypeScript, Node.js"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" disabled={!devForm.name.trim() || !devForm.role.trim()}>
                    {editingDev ? 'Save Changes' : 'Add Developer'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => { setShowDevForm(false); setEditingDev(null); }}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Developer list */}
            {developers.length === 0 && !showDevForm ? (
              <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
                <Code2 className="mx-auto h-10 w-10 text-slate-300 mb-3" />
                <p className="text-slate-500">No developers yet. Add your first team member.</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {developers.map((dev) => (
                  <div key={dev.id} className="rounded-lg border border-slate-200 bg-white p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={dev.name} size="md" />
                        <div>
                          <p className="font-semibold text-slate-900">{dev.name}</p>
                          <p className="text-sm text-slate-500">{dev.role}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openDevForm(dev)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => { if (window.confirm(`Remove ${dev.name}?`)) onDeleteDeveloper(dev.id); }}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs text-slate-500">
                      <div className="flex justify-between">
                        <span>Timezone</span>
                        <span className="font-medium text-slate-700">{dev.timezone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Hours</span>
                        <span className="font-medium text-slate-700">{dev.workingHours}</span>
                      </div>
                      {dev.email && (
                        <div className="flex justify-between">
                          <span>Email</span>
                          <span className="font-medium text-slate-700 truncate ml-2 max-w-[140px]">{dev.email}</span>
                        </div>
                      )}
                    </div>
                    {dev.skills.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {dev.skills.map((s) => (
                          <span key={s} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                    <p className="mt-3 text-xs text-slate-400">
                      Active on {tasks.filter((t) => t.assignedDeveloper?.id === dev.id && t.status !== 'approved').length} task(s)
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── AGENCY TAB ── */}
        {tab === 'agency' && (
          <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
            <div className="space-y-6">
              {/* Agency settings form */}
              <form onSubmit={handleAgencySave} className="rounded-lg border border-slate-200 bg-white p-6 space-y-5">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <div>
                    <h2 className="font-bold text-slate-900">Agency Settings</h2>
                    <p className="text-sm text-slate-500">Update the client's package and capacity details.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Input
                      label="Agency / Client Name"
                      value={agencyForm.name}
                      onChange={(e) => {
                        setAgencyForm({ ...agencyForm, name: e.target.value });
                      }}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Package Type</label>
                    <select
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={agencyForm.packageType}
                      onChange={(e) => {
                        const pkg = e.target.value;
                        const defaultHours = PACKAGE_HOURS[pkg] ?? 80;
                        setAgencyForm({ ...agencyForm, packageType: pkg, monthlyHours: String(defaultHours) });
                      }}
                    >
                      <option value="starter">Starter (40h/mo)</option>
                      <option value="growth">Growth (80h/mo)</option>
                      <option value="sprint">Sprint (160h/mo)</option>
                    </select>
                  </div>
                  <Input
                    label="Monthly Hours"
                    type="number"
                    min={0}
                    value={agencyForm.monthlyHours}
                    onChange={(e) => setAgencyForm({ ...agencyForm, monthlyHours: e.target.value })}
                  />
                  <Input
                    label="Hours Used"
                    type="number"
                    min={0}
                    value={agencyForm.hoursUsed}
                    onChange={(e) => setAgencyForm({ ...agencyForm, hoursUsed: e.target.value })}
                  />
                  <Input
                    label="Rollover Hours"
                    type="number"
                    min={0}
                    value={agencyForm.rolloverHours}
                    onChange={(e) => setAgencyForm({ ...agencyForm, rolloverHours: e.target.value })}
                  />
                  <Input
                    label="Extra Hour Rate ($/hr)"
                    type="number"
                    min={0}
                    value={agencyForm.extraHourRate}
                    onChange={(e) => setAgencyForm({ ...agencyForm, extraHourRate: e.target.value })}
                  />
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Renewal Date</label>
                    <input
                      type="date"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={agencyForm.renewalDate}
                      onChange={(e) => setAgencyForm({ ...agencyForm, renewalDate: e.target.value })}
                    />
                  </div>
                </div>

                <Button type="submit" isLoading={savingAgency} disabled={savingAgency}>
                  Save Agency Settings
                </Button>
              </form>

              {/* Current capacity summary */}
              {agency && (
                <div className="rounded-lg border border-slate-200 bg-white p-6">
                  <h2 className="font-bold text-slate-900 mb-4">Current Capacity</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    {[
                      { label: 'Monthly', value: `${agency.monthlyHours}h` },
                      { label: 'Used', value: `${agency.hoursUsed}h` },
                      { label: 'Remaining', value: `${agency.hoursRemaining}h` },
                      { label: 'Rollover', value: `${agency.rolloverHours}h` },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-lg bg-slate-50 p-3">
                        <p className="text-slate-500 text-xs">{label}</p>
                        <p className="font-bold text-slate-900 text-lg mt-0.5">{value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Hours used</span>
                      <span>{Math.round((agency.hoursUsed / agency.monthlyHours) * 100)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200">
                      <div
                        className="h-2 rounded-full bg-blue-500 transition-all"
                        style={{ width: `${Math.min(100, (agency.hoursUsed / agency.monthlyHours) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Send alert panel */}
            <div className="space-y-4">
              <form onSubmit={handleSendAlert} className="rounded-lg border border-slate-200 bg-white p-6 space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <BellPlus className="h-5 w-5 text-blue-600" />
                  <div>
                    <h2 className="font-bold text-slate-900">Send Client Alert</h2>
                    <p className="text-sm text-slate-500">Announcement visible to all clients.</p>
                  </div>
                </div>
                <Input
                  label="Alert title"
                  value={alertTitle}
                  onChange={(e) => setAlertTitle(e.target.value)}
                  placeholder="e.g. Scheduled maintenance tonight"
                  required
                />
                <Textarea
                  label="Message"
                  value={alertMessage}
                  onChange={(e) => setAlertMessage(e.target.value)}
                  placeholder="Write the update clients will see."
                  rows={4}
                  required
                />
                <Button
                  type="submit"
                  leftIcon={<BellPlus className="h-4 w-4" />}
                  isLoading={sendingAlert}
                  disabled={sendingAlert || !alertTitle.trim() || !alertMessage.trim()}
                >
                  Send Alert
                </Button>
              </form>

              {/* Recent alerts */}
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <h2 className="font-bold text-slate-900 mb-4">Recent Alerts</h2>
                {notifications.filter((n) => n.type === 'announcement').length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No announcements sent yet.</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {notifications.filter((n) => n.type === 'announcement').slice(0, 8).map((n) => (
                      <div key={n.id} className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                        <p className="font-medium text-slate-900 text-sm">{n.title}</p>
                        <p className="text-sm text-slate-600 mt-1">{n.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatDateRelative(n.timestamp)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Task detail modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onAction={handleTaskAction}
          isAdmin
          developers={developers}
        />
      )}
    </div>
  );
}
